// src/services/conversationApi.js
import axios from 'axios';
import { Auth } from 'aws-amplify';

// Configuration for the conversation API
const CONVERSATION_API_CONFIG = {
  dev: {
    baseUrl: 'https://conversation-dev.agentdeveloper.agent-enterprise.com',
    apiUrl: 'https://conversation-dev.agentdeveloper..agent-enterprise.com'
  },
  prod: {
    baseUrl: 'https://conversation.agentdeveloper.agent-enterprise.com',
    apiUrl: 'https://conversation.agentdeveloper.agent-enterprise.com'
  }
};

// Get the appropriate API URL based on environment
const getConversationApiUrl = (environment = 'prod') => {
  return CONVERSATION_API_CONFIG[environment]?.apiUrl || CONVERSATION_API_CONFIG.dev.apiUrl;
};

// Helper function to make authenticated requests to conversation API
export const makeConversationRequest = async (method, endpoint, payload = null, environment = 'prod') => {
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();

    const baseUrl = getConversationApiUrl(environment);
    const requestUrl = `${baseUrl}${endpoint}`;
    
    const response = await axios({
      method: method,
      url: requestUrl,
      headers: {
        'Authorization': idToken,
        'Content-Type': 'application/json'
      },
      data: payload,
      withCredentials: false,
      timeout: 45000 
    });

    return response.data;
  } catch (error) {
    console.error('Conversation API request error:', error);
    
    if (error.response && error.response.data) {
      throw new Error(error.response.data.error || error.response.data.message || 'API request failed');
    }
    
    throw error;
  }
};

// Parse response body that comes as JSON string
const parseResponseBody = (response) => {
  if (response.body && typeof response.body === 'string') {
    try {
      return JSON.parse(response.body);
    } catch (e) {
      return response;
    }
  }
  return response;
};

/**
 * Send a message (simplified - no polling)
 */
export const sendMessage = async (message, sessionId = null, userId = null) => {
  try {
    const payload = {
      message: message,
      ...(userId && { userId })
    };

    if (sessionId && sessionId.includes('temp-')) {
      payload.sessionIdTemp = sessionId;
    }
    if (sessionId && !sessionId.includes('temp-')) {
      payload.sessionId = sessionId;
    }

    console.log('📤 Sending message...');
    
    const response = await makeConversationRequest('POST', '/conversation', payload);
    const parsedResponse = parseResponseBody(response);

    console.log('📥 Response received:', parsedResponse.statusCode || response.statusCode);

    // Return immediate response - WebSocket will handle updates
    return {
      success: true,
      status: parsedResponse.status || 'processing',
      statusCode: parsedResponse.statusCode || response.statusCode || 200,
      response: parsedResponse.response || parsedResponse.message || 'Message sent successfully',
      sessionId: parsedResponse.sessionId || sessionId,
      title: parsedResponse.title,
      responseMessageId: parsedResponse.messageId || parsedResponse.responseMessageId || Date.now().toString(),
      responseTimestamp: parsedResponse.timestamp || parsedResponse.responseTimestamp || new Date().toISOString(),
      intent: parsedResponse.intent || 'GENERAL_QUESTION',
      ticketId: parsedResponse.ticketId,
      executionArn: parsedResponse.executionArn
    };
    
  } catch (error) {
    console.error('❌ Error sending message:', error);
    throw error;
  }
};

/**
 * Get conversation history
 */
export const getConversationHistory = async (sessionId) => {
  try {
    if (!sessionId || typeof sessionId !== 'string') {
      return { success: false, messages: [], sessionId: sessionId, error: 'Invalid session ID' };
    }
    
    const response = await makeConversationRequest('POST', '/conversation/history', { sessionId: sessionId });
    const parsedResponse = parseResponseBody(response);
    
    if (parsedResponse.success && parsedResponse.messages) {
      const formattedMessages = parsedResponse.messages.map(msg => ({
        id: msg.messageId || msg.id || Date.now() + Math.random(),
        content: msg.content || msg.message,
        role: msg.role || msg.sender,
        timestamp: new Date(msg.timestamp || msg.createdAt || Date.now()),
        intent: msg.intent,
        ticketId: msg.ticketId,
        executionArn: msg.executionArn
      }));
      
      return {
        success: true,
        messages: formattedMessages,
        sessionId: parsedResponse.sessionId || sessionId,
        sessionMetadata: parsedResponse.sessionMetadata
      };
    }
    
    return { success: true, messages: [], sessionId: sessionId, error: null };
  } catch (error) {
    return { success: false, messages: [], sessionId: sessionId, error: error.message };
  }
};

/**
 * Get user conversations
 */
export const getUserConversations = async (userId = null) => {
  try {
    const payload = userId ? { userId } : {};
    const response = await makeConversationRequest('POST', '/conversations/list', payload);
    const parsedResponse = parseResponseBody(response);
    
    if (parsedResponse.success && parsedResponse.conversations) {
      return { 
        success: true, 
        conversations: parsedResponse.conversations, 
        count: parsedResponse.count 
      };
    }
    
    return { success: true, conversations: [], count: 0, error: null };
  } catch (error) {
    return { success: false, conversations: [], count: 0, error: error.message };
  }
};

/**
 * Get user tasks
 */
export const getUserTasks = async (sessionId = null, status = null) => {
  try {
    const payload = {};
    if (sessionId) payload.sessionId = sessionId;
    if (status) payload.status = status;
    
    const response = await makeConversationRequest('POST', '/tasks/list', payload);
    const parsedResponse = parseResponseBody(response);
    
    if (parsedResponse.success && parsedResponse.tasks) {
      const formattedTasks = parsedResponse.tasks.map(task => ({
        ticketId: task.ticketId,
        title: task.title || task.details?.description || 'Untitled Task',
        status: task.status || 'CREATED',
        createdAt: task.createdAt,
        updatedAt: task.updatedAt,
        description: task.details?.description || task.description || 'No description available',
        details: task.details || {},
        sessionId: task.sessionId,
        executionArn: task.executionArn,
        priority: task.details?.priority || task.priority || 'medium'
      }));
      
      return { success: true, tasks: formattedTasks, count: parsedResponse.count };
    }
    
    return { success: true, tasks: [], count: 0, error: null };
  } catch (error) {
    return { success: false, tasks: [], count: 0, error: error.message };
  }
};

/**
 * Execute a task
 */
export const executeTask = async (ticketId) => {
  try {
    const response = await makeConversationRequest('POST', '/tasks/execute', { ticketId: ticketId });
    const parsedResponse = parseResponseBody(response);
    
    return {
      success: parsedResponse.success !== false,
      message: parsedResponse.message || response.message || 'Task execution started',
      executionArn: parsedResponse.executionArn || response.executionArn
    };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

/**
 * Get task status
 */
export const getTaskStatus = async (ticketId) => {
  try {
    const response = await makeConversationRequest('POST', '/tasks/status', { ticketId: ticketId });
    const parsedResponse = parseResponseBody(response);
    
    return {
      success: parsedResponse.success !== false,
      status: parsedResponse.status,
      details: parsedResponse.details,
      error: parsedResponse.error || null
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default {
  sendMessage,
  getConversationHistory,
  getUserConversations,
  getUserTasks,
  executeTask,
  getTaskStatus
};
